import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import { useIdleLogout } from "@/lib/useIdleLogout";
import { useNotifications } from "@/context/NotificationContext";
import { ROLE_LABELS } from "@/lib/api";
import {
  LayoutDashboard, Users, FlaskConical, ScrollText, Settings,
  LogOut, ShieldCheck, Menu, X, MessageSquare, FileWarning, CalendarDays, Sun, TrendingUp} from "lucide-react";
import { cn } from "@/lib/utils";
import RosMark from "@/components/RosMark";
import ClinicMark from "@/components/ClinicMark";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview", end: true },
  { to: "/today", label: "Today", icon: Sun, testid: "nav-today" },
  { to: "/patients", label: "Patients", icon: Users, testid: "nav-patients" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/labs", label: "Lab Review", icon: FlaskConical, testid: "nav-labs" },
  // The only nav item carrying a count. It is the one place in the sidebar where
  // something is waiting on a person rather than merely existing.
  { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", badge: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit", roles: ["owner", "provider", "system_admin"] },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance", roles: ["owner", "system_admin"] },
  { to: "/incidents", label: "Incidents", icon: FileWarning, testid: "nav-incidents", roles: ["owner", "provider", "system_admin"] },
  { to: "/snapshot", label: "Snapshot", icon: TrendingUp, testid: "nav-snapshot", roles: ["owner", "system_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { clinic } = useClinic();
  const { badge } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Staff sessions auto-logoff after 15 minutes of inactivity (HIPAA).
  useIdleLogout({ minutes: 15, onIdle: () => navigate("/login?expired=1") });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("");
  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  const clinicName = clinic?.name || "RelationshipOS";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static z-40 inset-y-0 left-0 w-64 bg-primary text-primary-foreground flex flex-col transition-transform",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
              <ClinicMark className="text-sm" />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-bold text-base leading-tight truncate" data-testid="clinic-name">{clinicName}</p>
              <p className="text-[11px] text-white/50 tracking-wide truncate">{clinic?.tagline || "Patient Operations"}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200",
                  isActive ? "bg-white/15 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                )
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              <span className="flex-1">{item.label}</span>
              {item.badge && badge > 0 && (
                <span
                  data-testid="nav-messages-badge"
                  aria-label={`${badge} waiting on a reply`}
                  className="shrink-0 min-w-[20px] text-center text-[11px] font-semibold tabular-nums rounded-full bg-white text-primary px-1.5 py-0.5">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/10">
          <div className="flex items-center gap-2.5 px-2 py-1.5 text-[11px] text-white/50">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            HIPAA-secured session
          </div>
          <p className="px-2 pt-1 text-[10px] text-white/30 tracking-wide inline-flex items-center gap-1.5">
            <RosMark size={12} dark /> Powered by RelationshipOS
          </p>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <button
            data-testid="sidebar-toggle"
            className="lg:hidden p-2 -ml-2 text-foreground"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden sm:block">
            <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{clinicName}</p>
          </div>
          <div className="flex items-center gap-3" data-testid="user-menu">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-sm font-semibold font-heading">
              {initials}
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
              title="Sign out"
            >
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
